/**
 * RaceGame — Plan 08-03 Task 2 (GAME-04).
 *
 * 말 4마리 경주 + 후원 메시지 parse (말 번호) + 베팅 리더보드.
 *
 * 결정론:
 *   - 메시지 parse 실패 시 createRaceAssignRng(eventId) 로 말 1~4 배정.
 *   - 베팅 누적 → bets Map<horseId, totalKrw>. 동일 eventId 중복 방어는 engine dedupe.
 *   - 결승선 winner 결정은 누적 베팅 비율로 최다 베팅 말 = 선두 (완전 결정적).
 *
 * 확률 공시:
 *   - "경주는 누적 후원액 상위 말이 선두를 유지. 무작위 말 배정은 메시지에 말 번호(1~4) 미포함 시."
 *
 * 사행성 차단:
 *   - winner 결정은 **베팅 비율** (확률 가중치 아닌 단순 합계) — 운이 아니라 집계.
 *   - reportResult 는 리더보드 snapshot 만 반환 (현금 환원 없음).
 */
import type { Container } from 'pixi.js';
import { Container as PixiContainer, Graphics, Text, TextStyle } from 'pixi.js';
import type {
  DonationEvent,
  Game,
  GameContext,
  GameMeta,
} from '../game-interface.js';
import type { RaceConfig } from '@loop/contracts/rest';
import { renderDisclosure } from '../probability-disclosure.js';
import { createRaceAssignRng } from './race.rng.js';

const RACE_DISCLOSURE =
  '경주는 누적 후원액 상위 말이 선두를 유지합니다. 메시지에 말 번호(1~4)가 없으면 무작위 배정됩니다. 현금 환원 없음.';

export interface LeaderboardEntry {
  readonly horseId: number;
  readonly totalKrw: number;
}

export class RaceGame implements Game {
  readonly meta: GameMeta;
  private container: Container | null = null;
  private ctx: GameContext | null = null;
  private readonly bets = new Map<number, number>();
  private readonly horseRegex: RegExp;
  private horseSprites: Graphics[] = [];

  constructor(private readonly config: RaceConfig) {
    this.horseRegex = new RegExp(config.messageParseRegex, 'i');
    this.meta = {
      kind: 'race',
      name: '경주',
      version: '1.0.0',
      prizes: Array.from({ length: config.horses }, (_, i) => ({
        id: `horse-${i + 1}`,
        name: `${i + 1}번 말`,
      })),
      probabilityDisclosure: RACE_DISCLOSURE,
    };
  }

  /**
   * 메시지에서 말 번호 parse. 1~config.horses 범위 외면 null.
   *
   * HI-02 ReDoS 방어: 입력 메시지 길이를 512자로 cap 하여 catastrophic backtracking
   * 시간 복잡도를 상수로 제한. Zod refine 이 1차 차단하나 다중 방어.
   */
  parseHorseId(message: string): number | null {
    if (!message) return null;
    const capped = message.length > 512 ? message.slice(0, 512) : message;
    const m = this.horseRegex.exec(capped);
    if (!m || !m[1]) return null;
    const id = Number.parseInt(m[1], 10);
    if (!Number.isFinite(id) || id < 1 || id > this.config.horses) return null;
    return id;
  }

  async init(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    const container = new PixiContainer();
    this.container = container;
    ctx.stage.addChild(container);

    const palette = [0xef4444, 0x10b981, 0x3b82f6, 0xf59e0b];
    const labelStyle = new TextStyle({
      fontFamily: 'system-ui, sans-serif',
      fontSize: 16,
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 2 },
    });
    const laneHeight = 90;
    const startX = 40;
    const startY = 80;
    for (let i = 0; i < this.config.horses; i += 1) {
      const lane = new Graphics();
      lane.rect(startX, startY + i * laneHeight, 700, laneHeight - 10).fill({
        color: 0x1f2937,
        alpha: 0.3,
      });
      container.addChild(lane);
      const horse = new Graphics();
      horse.circle(0, 0, 20).fill({ color: palette[i] });
      horse.x = startX + 20;
      horse.y = startY + i * laneHeight + (laneHeight - 10) / 2;
      container.addChild(horse);
      this.horseSprites.push(horse);
      const label = new Text({
        text: `${i + 1}번 말`,
        style: labelStyle,
      });
      label.x = startX - 30;
      label.y = horse.y - 10;
      container.addChild(label);
    }

    renderDisclosure(ctx.stage, this.meta.probabilityDisclosure, {
      reducedMotion: ctx.reducedMotion,
      position: 'bottom',
    });
  }

  getLeaderboard(): readonly LeaderboardEntry[] {
    return Array.from(this.bets.entries())
      .map(([horseId, totalKrw]) => ({ horseId, totalKrw }))
      .sort((a, b) => b.totalKrw - a.totalKrw);
  }

  async onDonation(event: DonationEvent): Promise<void> {
    if (!this.ctx) return;
    let horseId = this.parseHorseId(event.message);
    if (horseId === null) {
      const r = createRaceAssignRng(event.eventId).next();
      horseId = Math.floor(r * this.config.horses) + 1;
    }
    this.bets.set(horseId, (this.bets.get(horseId) ?? 0) + event.amount);

    // 말 위치 업데이트 — 누적 베팅 비율로 x 좌표 갱신.
    const leaderboard = this.getLeaderboard();
    const maxKrw = leaderboard[0]?.totalKrw ?? 1;
    const trackWidth = 660; // lane width - horse radius margin
    for (let i = 0; i < this.horseSprites.length; i += 1) {
      const id = i + 1;
      const total = this.bets.get(id) ?? 0;
      const ratio = maxKrw > 0 ? total / maxKrw : 0;
      // reducedMotion=true 는 즉시 위치, false 는 동일하나 애니메이션은 추후 E2E.
      const sprite = this.horseSprites[i];
      if (sprite) sprite.x = 60 + ratio * trackWidth;
    }

    this.ctx.reportResult({
      kind: 'race',
      winner: { horseId, leaderboard: leaderboard.slice(0, 3) },
      donationEventId: event.eventId,
      at: new Date().toISOString(),
    });
  }

  destroy(): void {
    if (this.container) {
      this.container.removeFromParent();
      this.container.destroy({ children: true });
      this.container = null;
    }
    this.horseSprites = [];
    this.bets.clear();
    this.ctx = null;
  }
}
